/**
 * Les pièces EN PLUS d'une machine, demandées pendant la CRÉATION de l'intervention (ADR-0016).
 *
 * L'intervention n'existe pas encore : rien n'est écrit ici, la liste vit dans le volet et part avec « Créer l'intervention »
 * (une écriture par machine, juste après la création, qui garde les pièces du palier et ajoute celles-ci à la suite). Même saisie
 * que dans le détail d'une intervention : référence choisie par son début dans le tarif de la marque de la machine, désignation,
 * quantité ; aucun montant.
 */
import { useState } from 'react';

import { AJOUT_VIDE, ajouter, designationApresChoix, type EntreeAjout, type Liste } from '../domaine/lignes-intervention';
import { ChampReference } from './ChampReference';

export function PiecesDemandeesALaCreation({
  machineId,
  pieces,
  desactive,
  onChange,
}: {
  machineId: string;
  pieces: Liste;
  desactive: boolean;
  onChange: (pieces: Liste) => void;
}) {
  const [saisie, setSaisie] = useState<EntreeAjout>({ ...AJOUT_VIDE, mode: 'LIBRE' });
  const [refus, setRefus] = useState<string | null>(null);
  const [posee, setPosee] = useState<string | null>(null);

  const champ = (cle: 'reference' | 'designation' | 'quantite', valeur: string) => {
    setRefus(null);
    setSaisie((s) => ({ ...s, [cle]: valeur }));
  };
  const ajouterLaPiece = () => {
    // Une référence tapée ou choisie est une pièce du tarif ; sans référence, c'est une pièce hors tarif (texte libre).
    const r = ajouter(pieces, { ...saisie, mode: saisie.reference.trim() === '' ? 'LIBRE' : 'TARIF' });
    if (!r.ok) {
      setRefus(r.message);
      return;
    }
    onChange(r.lignes);
    setSaisie({ ...AJOUT_VIDE, mode: 'LIBRE' });
    setPosee(null);
    setRefus(null);
  };

  return (
    <div className="planning-pieces-plus visite-pieces-plus">
      <div className="planning-pieces-entete">
        <strong>Pièces en plus</strong>
        <span className="aide">ce que la gamme ne dit pas (un capteur, une pièce demandée), sans montant</span>
      </div>
      {pieces.length > 0 && (
        <ul className="visite-pieces-attente">
          {pieces.map((p, i) => (
            <li key={`${p.reference ?? ''}|${p.designation}|${i}`}>
              <span>
                {p.reference ? <span className="ref">{p.reference} · </span> : null}
                {p.designation} × {p.quantity ?? '?'}
              </span>
              <button
                type="button"
                className="secondaire discret"
                disabled={desactive}
                aria-label={`Retirer ${p.designation}`}
                title="Retirer cette pièce"
                onClick={() => onChange(pieces.filter((_, j) => j !== i))}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="planning-pieces-ajout">
        <label className="libelle-piece">Référence de la pièce <span className="aide">(tape le début : la liste se réduit)</span></label>
        <ChampReference
          machineId={machineId}
          libelle="Référence de la pièce à ajouter"
          valeur={saisie.reference}
          placeholder="Début de la référence (facultatif)"
          desactive={desactive}
          onChange={(valeur) => champ('reference', valeur)}
          onChoisir={(choix) => {
            setRefus(null);
            setSaisie((s) => ({ ...s, reference: choix.reference, designation: designationApresChoix(s.designation, posee, choix.description) }));
            setPosee(choix.description);
          }}
        />
        <div className="planning-pieces-ajout-ligne">
          <div className="champ-piece champ-piece-designation">
            <label htmlFor={`piece-designation-${machineId}`} className="libelle-piece">Désignation</label>
          <input
            id={`piece-designation-${machineId}`}
            type="text"
            aria-label="Désignation de la pièce à ajouter"
            placeholder="Désignation"
            value={saisie.designation}
            disabled={desactive}
            maxLength={300}
            onChange={(e) => champ('designation', e.target.value)}
          />
          </div>
          <div className="champ-piece">
            <label htmlFor={`piece-quantite-${machineId}`} className="libelle-piece">Quantité</label>
          <input
            id={`piece-quantite-${machineId}`}
            type="text"
            inputMode="decimal"
            aria-label="Quantité de la pièce à ajouter"
            placeholder="Qté"
            className="q"
            value={saisie.quantite}
            disabled={desactive}
            onChange={(e) => champ('quantite', e.target.value)}
          />
          </div>
          <button type="button" className="secondaire" disabled={desactive} onClick={ajouterLaPiece}>
            Ajouter à l’intervention
          </button>
        </div>
        {refus && <p className="aide erreur-champ">{refus}</p>}
        <p className="aide">Elles sont enregistrées avec l’intervention, au moment de « Créer l’intervention ».</p>
      </div>
    </div>
  );
}
